import React, { useEffect, useMemo, useState } from 'react';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { SectionCard } from '../components/SectionCard';
import { showError, showInfo } from '../lib/alerts';
import { WorkspaceOption } from '../types';

interface LogsPageProps {
  supabase: SupabaseClient;
  session: Session | null;
}

interface ActivityRow {
  id: string;
  userId: string;
  userName: string;
  userRole: string;
  filePath: string;
  eventType: string;
  eventCount: number;
  activeSeconds: number;
  occurredAt: string;
}

function getDisplayName(fullName: string | null | undefined, email: string | null | undefined): string {
  // Use full name if available
  if (fullName && fullName.trim()) {
    return fullName;
  }
  
  // Extract name from email (before @)
  if (email) {
    const namePart = email.split('@')[0];
    // Convert mathewjollyavji172005 to Mathew Jolly Avji
    return namePart
      .replace(/(\d+)$/, '') // Remove trailing numbers
      .replace(/([A-Z])/g, ' $1') // Add space before capitals
      .split(/[._-]/) // Split by common delimiters
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ')
      .trim() || email;
  }
  
  return 'Unknown User';
}

function formatActiveTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export default function LogsPage({ supabase, session }: LogsPageProps) {
  const [memberships, setMemberships] = useState<WorkspaceOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [recentActivity, setRecentActivity] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);

  const selectedMembership = useMemo(
    () => memberships.find((membership) => membership.projectId === selectedProjectId) || memberships[0] || null,
    [memberships, selectedProjectId]
  );

  useEffect(() => {
    if (!session) {
      return;
    }

    const userId = session.user.id;

    let cancelled = false;

    async function loadLogs() {
      setLoading(true);
      try {
        showInfo('Loading activity logs...');
        const { data: memberRows, error: memberError } = await supabase
          .from('project_members')
          .select('workspace_id, project_id, role')
          .eq('user_id', userId);

        if (memberError) throw memberError;

        const membershipsResult = (memberRows || []) as Array<{ workspace_id: string; project_id: string; role: string }>;
        const projectIds = Array.from(new Set(membershipsResult.map((row) => row.project_id).filter(Boolean)));
        const workspaceIds = Array.from(new Set(membershipsResult.map((row) => row.workspace_id).filter(Boolean)));

        const projectRows: Array<{ id: string; name: string; workspace_id: string }> = [];
        const workspaceRows: Array<{ id: string; name: string }> = [];

        if (projectIds.length) {
          const { data, error } = await supabase.from('projects').select('id, name, workspace_id').in('id', projectIds);
          if (error) throw error;
          projectRows.push(...((data || []) as Array<{ id: string; name: string; workspace_id: string }>));
        }

        if (workspaceIds.length) {
          const { data, error } = await supabase.from('workspaces').select('id, name').in('id', workspaceIds);
          if (error) throw error;
          workspaceRows.push(...((data || []) as Array<{ id: string; name: string }>));
        }

        if (cancelled) return;

        const projectsById = new Map(projectRows.map((project) => [project.id, project] as const));
        const workspacesById = new Map(workspaceRows.map((workspace) => [workspace.id, workspace] as const));

        const nextMemberships = membershipsResult.map((row) => ({
          workspaceId: row.workspace_id,
          workspaceName: workspacesById.get(row.workspace_id)?.name || 'Workspace',
          projectId: row.project_id,
          projectName: projectsById.get(row.project_id)?.name || 'Project',
          role: row.role,
        }));

        setMemberships(nextMemberships);
        const nextProjectId = selectedProjectId || nextMemberships[0]?.projectId || '';
        setSelectedProjectId(nextProjectId);

        if (!nextProjectId) {
          setRecentActivity([]);
          return;
        }

        const { data: activityRows, error: activityError } = await supabase
          .from('activity_logs')
          .select('id, user_id, file_path, event_type, event_count, active_seconds, occurred_at')
          .eq('project_id', nextProjectId)
          .order('occurred_at', { ascending: false })
          .limit(80);

        if (activityError) throw activityError;

        const activities = (activityRows || []) as Array<{
          id: string;
          user_id: string;
          file_path: string;
          event_type: string;
          event_count: number;
          active_seconds: number;
          occurred_at: string;
        }>;

        const userIds = Array.from(new Set(activities.map((row) => row.user_id).filter(Boolean)));
        const usersById = new Map<string, { full_name: string | null; email: string }>();
        const userRolesById = new Map<string, string>();

        if (userIds.length) {
          // Use RPC function to bypass RLS for user data
          const { data: userRows, error: userError } = await supabase.rpc('get_users_for_project', {
            project_id: nextProjectId,
            user_ids: userIds
          });
          if (userError) {
            console.error('Error fetching users via RPC:', userError);
            showError(`Failed to load user names: ${userError.message}`);
          } else if (userRows && Array.isArray(userRows)) {
            for (const user of userRows) {
              usersById.set(user.id, { full_name: user.full_name, email: user.email });
            }
          }

          // Use RPC function to get user roles
          const { data: roleRows, error: roleError } = await supabase.rpc('get_user_roles', {
            project_id: nextProjectId,
            user_ids: userIds
          });
          if (roleError) {
            console.error('Error fetching user roles via RPC:', roleError);
          } else if (roleRows && Array.isArray(roleRows)) {
            for (const roleRow of roleRows) {
              userRolesById.set(roleRow.user_id, roleRow.role);
            }
          }
        }

        if (cancelled) return;

        setRecentActivity(
          activities.map((row) => {
            const userInfo = usersById.get(row.user_id);
            return {
              id: row.id,
              userId: row.user_id,
              userName: getDisplayName(userInfo?.full_name, userInfo?.email),
              userRole: userRolesById.get(row.user_id) || 'member',
              filePath: row.file_path,
              eventType: row.event_type,
              eventCount: row.event_count ?? 0,
              activeSeconds: row.active_seconds ?? 0,
              occurredAt: row.occurred_at,
            };
          })
        );
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load log stream');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadLogs();

    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, session, supabase]);

  return (
    <div className="dashboard-content dashboard-content--single">
      <div className="section-heading-row section-heading-row--spread">
        <div>
          <div className="section-kicker">LOG STREAM</div>
          <h2>Recent activity across the selected project.</h2>
        </div>
        <label className="topbar-select">
          <span>Project</span>
          <select 
            value={selectedProjectId} 
            onChange={(event) => setSelectedProjectId(event.target.value)}
            disabled={loading || memberships.length === 0}
          >
            {loading ? (
              <option value="">Loading projects...</option>
            ) : memberships.length === 0 ? (
              <option value="">No projects found</option>
            ) : (
              memberships.map((membership) => (
                <option key={membership.projectId} value={membership.projectId}>
                  {membership.workspaceName} / {membership.projectName}
                </option>
              ))
            )}
          </select>
        </label>
      </div>

      <SectionCard title="Recent Activity" subtitle={selectedMembership ? `${selectedMembership.workspaceName} / ${selectedMembership.projectName}` : 'No project selected'}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Event</th>
                <th>File</th>
                <th>Active Time</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {recentActivity.map((activity) => (
                <tr key={activity.id}>
                  <td>{new Date(activity.occurredAt).toLocaleString()}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span>{activity.userName}</span>
                      <span style={{
                        background: activity.userRole === 'owner' 
                          ? 'rgba(255, 215, 0, 0.2)'
                          : activity.userRole === 'admin'
                          ? 'rgba(125, 227, 199, 0.2)'
                          : 'rgba(100, 150, 200, 0.15)',
                        color: activity.userRole === 'owner' 
                          ? '#FFD700'
                          : activity.userRole === 'admin'
                          ? '#7de3c7'
                          : '#6BB6FF',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        border: activity.userRole === 'owner' 
                          ? '1px solid rgba(255, 215, 0, 0.4)'
                          : activity.userRole === 'admin'
                          ? '1px solid rgba(125, 227, 199, 0.4)'
                          : '1px solid rgba(100, 150, 200, 0.3)',
                        display: 'inline-block'
                      }}>
                        {activity.userRole === 'owner' ? '👑 Lead' : activity.userRole === 'admin' ? '⭐ Admin' : '👤 Member'}
                      </span>
                    </div>
                  </td>
                  <td>{activity.eventType}</td>
                  <td>{activity.filePath}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{formatActiveTime(activity.activeSeconds)}</td>
                  <td>{activity.eventCount ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading ? <div className="muted" style={{ marginTop: 12 }}>Loading activity...</div> : null}
      </SectionCard>
    </div>
  );
}
